import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { getTemplateComponents } from './../template_helpers';
//Types
import type {
	TemplateService,
	VirtualComponentInterfaceData,
	VCInterfaceStatus,
	VirtualComponentTypes,
	TemplateComponent,
} from 'src/types/VirtualComponents';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { Device } from 'src/types/Device';

export const updateIrrigationControllerStatus = (
	interface_data: VCInterfaceStatus[],
	data: VCInterfaceStatus,
	type: VirtualComponentTypes,
	valueData: VirtualComponentInterfaceData,
	templateService: TemplateService,
	deviceStatus: DeviceStatusG2,
): void => {
	let zoneData: VCInterfaceStatus = data;
	if (type === 'boolean' && /^zone/.test(data.role)) {
		const channel = Number(data.role.split('zone')[1]);
		const zoneSettings = templateService?.settings?.zones?.[channel];
		zoneData = {
			...zoneData,
			name: `zone:${channel}`,
			value: zoneSettings?.name || data.vcName,
			event: valueData.event,
			icon: `fa${valueData.event ? 's' : 'r'} fa-droplet`,
			hasAction: false,
			settings: zoneSettings,
			timer: null,
		};
		const channelData = deviceStatus?.['object:200']?.value?.[`zone${channel}`];
		if (channelData) {
			zoneData.settings = zoneData.settings || {
				duration: 0,
				water_amount: 0,
				source: '',
			};
			zoneData.settings.source = channelData?.source;
			zoneData.timer = channelData;
		}
	} else if (/^(average_temperature|last_precipitation)$/.test(data.role)) {
		zoneData.hideLabel = templateService?.settings?.weather_api !== true;
	} else if (/^(zones_status)$/.test(data.role)) {
		return;
	}
	interface_data.push(zoneData);
};

export const isSequenceRunning = (device: Device): boolean => {
	const components: TemplateComponent[] = getTemplateComponents(device, null, /^active_sequence$/);
	return components[0] && components[0]?.status?.value !== 'none';
};

export const irrigationZoneColors = ['#2366ff', '#3fa6ff', '#01d7d9', '#935aff', '#ffb502', '#ff5126'];

export const getZoneDuration = (
	zone: VCInterfaceStatus,
): {
	title: string;
	additionalTitle: string;
	additionalDuration: number;
	duration: number;
} => {
	const $format = get(format);
	const minutes = $format('irrigation_labellist_value_min', {
		default: 'min',
	});
	const zoneTimerDuration = zone.timer.duration;
	const zoneConfigDuration = zone.settings.duration;
	const additionalDuration = zoneTimerDuration - zoneConfigDuration;
	const title = `${zoneTimerDuration} ${minutes}`;
	const zoneDuration = {
		title,
		additionalDuration,
		additionalTitle: title,
		duration: zoneTimerDuration,
	};
	if (additionalDuration !== 0) {
		const additionalSign = `${additionalDuration > 0 ? '+' : '-'}`;
		zoneDuration.additionalTitle = `${zoneConfigDuration} ${minutes} ${additionalSign} ${Math.abs(additionalDuration)} ${minutes}`;
	}
	return zoneDuration;
};

export type ButtonTimer = { time: number; percentage: number };

export class ZoneTimer {
	private readonly _callback: (t: ButtonTimer) => void;
	private _interval: ReturnType<typeof setInterval> = null;
	private _durationSec: number = 0;
	private _remainingSec: number = 0;
	private _running: boolean = false;

	constructor(callback: (t: ButtonTimer) => void) {
		this._callback = callback;
	}
	public start(timer: VCInterfaceStatus['timer']) {
		this.stop();
		this._durationSec = Math.max(0, Math.round(timer.duration * 60));
		const elapsedMs = Date.now() - timer.started_at;
		this._remainingSec = Math.max(0, Math.ceil(this._durationSec - elapsedMs / 1000));
		this._emit(this._remainingSec);
		if (this._durationSec === 0 || this._remainingSec === 0) {
			this._running = false;
			return;
		}
		this._running = true;
		this._interval = setInterval(() => this._tick(), 1000);
	}
	public get isRunning() {
		return this._running;
	}
	public stop() {
		if (this._interval) {
			clearInterval(this._interval);
			this._interval = null;
		}
		this._running = false;
	}
	public reset() {
		this.stop();
		this._durationSec = 0;
		this._remainingSec = 0;
		this._emit(0);
	}
	private _tick() {
		if (!this._running) return;
		this._remainingSec = Math.max(0, this._remainingSec - 1);
		this._emit(this._remainingSec);
		if (this._remainingSec <= 0) {
			this.stop();
		}
	}
	protected _emit(remaining: number) {
		const percentage =
			this._durationSec > 0 ? Math.round((remaining / this._durationSec) * 100) : 100;
		this._callback({ time: remaining, percentage });
	}
}
